import { communicationService } from './communicationService';
import { logger } from './utils';

/**
 * Domain-specific service for character operations
 * Uses the unified communication service
 */
export class CharacterService {
  constructor(commsService) {
    this.http = commsService.httpClient;
  }
  
  /**
   * Fetch all characters for the user
   * @returns {Promise<Array>} Array of characters
   */
  async getCharacters() {
    try {
      logger.info('CharacterService', 'Fetching characters');
      const response = await this.http.get('/characters/');
      logger.debug('CharacterService', `Retrieved ${response.data?.length || 0} characters`);
      return response.data || [];
    } catch (error) {
      logger.error('CharacterService', 'Failed to fetch characters', error);
      throw error;
    }
  }
  
  /**
   * Create a new character
   * @param {Object} characterData - Character data to create
   * @returns {Promise<Object>} The created character
   */
  async createCharacter(characterData) {
    try {
      logger.info('CharacterService', 'Creating new character');
      const response = await this.http.post('/characters/', characterData);
      logger.debug('CharacterService', 'Character created successfully', response.data);
      return response.data;
    } catch (error) {
      logger.error('CharacterService', 'Failed to create character', error);
      throw error;
    }
  }
  
  /**
   * Update an existing character
   * @param {string|number} characterId - ID of the character to update
   * @param {Object} characterData - Updated character data
   * @returns {Promise<Object>} The updated character
   */
  async updateCharacter(characterId, characterData) {
    try {
      logger.info('CharacterService', `Updating character ${characterId}`);
      const response = await this.http.put(`/characters/${characterId}/`, characterData);
      logger.debug('CharacterService', 'Character updated successfully', response.data);
      return response.data;
    } catch (error) {
      logger.error('CharacterService', `Failed to update character ${characterId}`, error);
      throw error;
    }
  }
  
  /**
   * Delete a character
   * @param {string|number} characterId - ID of the character to delete
   * @returns {Promise<void>}
   */
  async deleteCharacter(characterId) {
    try {
      logger.info('CharacterService', `Deleting character ${characterId}`);
      await this.http.delete(`/characters/${characterId}/`);
      logger.debug('CharacterService', 'Character deleted successfully');
    } catch (error) {
      logger.error('CharacterService', `Failed to delete character ${characterId}`, error);
      throw error;
    }
  }
  
  /**
   * Get a character by ID
   * @param {string|number} characterId - ID of the character to fetch
   * @returns {Promise<Object>} The character
   */
  async getCharacter(characterId) {
    try {
      logger.info('CharacterService', `Fetching character ${characterId}`);
      const response = await this.http.get(`/characters/${characterId}/`);
      logger.debug('CharacterService', 'Character fetched successfully', response.data);
      return response.data;
    } catch (error) {
      logger.error('CharacterService', `Failed to fetch character ${characterId}`, error);
      throw error;
    }
  }
}

// Export singleton
export const characterService = new CharacterService(communicationService);